// src/components/practice/tasks/SplitArticleComprehension.jsx
import React from 'react';
import { FlagButton } from '../PartDisplay';

/**
 * FINAL - Newspaper Style Layout (Fully Dynamic from JSON)
 * - Centered title from JSON
 * - Drop cap (big first letter) for content
 * - 3-column newspaper layout
 * - Dynamic source citation from JSON
 */
const SplitArticleComprehension = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  toggleFlagQuestion,
  ...props
}) => {
  const texts = partData.content?.texts || [];
  const allProps = { flaggedQuestions, toggleFlagQuestion, ...props };

  // Function to render content with drop cap (big first letter)
  const renderContentWithDropCap = (content) => {
    if (!content || content.length === 0) return null;

    const firstLetter = content.charAt(0);
    const restOfContent = content.slice(1);

    return (
      <div
        className="text-gray-800 leading-relaxed"
        style={{
          columnCount: 3,
          columnGap: '2rem',
          fontSize: '14px',
          lineHeight: '1.6',
          textAlign: 'justify'
        }}
      >
        {/* Drop Cap - Big First Letter */}
        <span
          style={{
            float: 'left',
            fontSize: '3.5rem',
            lineHeight: '3rem',
            fontWeight: 'bold',
            marginRight: '0.5rem',
            marginTop: '0.2rem'
          }}
          className="text-gray-900"
        >
          {firstLetter}
        </span>
        {restOfContent}
      </div>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      <div className="space-y-8">
        {texts.map(text => {
          const associatedExamples = examples.filter(q => q.associatedTextId === text.id);
          const associatedQuestions = questions.filter(q => q.associatedTextId === text.id);

          return (
            <div key={text.id} className="grid grid-cols-1 md:grid-cols-5 md:gap-8">

              {/* LEFT COLUMN - Newspaper Style Article */}
              <div className="md:col-span-3">
                <div className="bg-white p-8 shadow-sm border border-gray-200">

                  {/* Title - CENTERED (from JSON) */}
                  <h3 className="text-2xl font-bold text-gray-900 text-center mb-6 pb-4 border-b border-gray-300">
                    {text.title}
                  </h3>

                  {/* Content with Drop Cap (from JSON) */}
                  {renderContentWithDropCap(text.content)}

                  {/* Source citation (from JSON if available) */}
                  {text.source && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 text-right italic">
                        {text.source}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN - Questions */}
              <div className="md:col-span-2 mt-6 md:mt-0">
                {(associatedExamples.length > 0 || associatedQuestions.length > 0) && (
                  <div className="space-y-1">

                    {/* Header */}
                    <div className="border-2 border-gray-900 bg-gray-100 p-3 rounded-none mb-4">
                      <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
                        Fragen zu: {text.title}
                      </h4>
                    </div>

                    {/* Examples */}
                    {associatedExamples.map(ex => {
                      const correctAnswer = ex.correctAnswer;
                      return (
                        <div
                          key={ex.questionId}
                          className="border border-t-0 border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1 flex items-start">
                              <span className="font-bold text-sm mr-2 pt-0.5">
                                {ex.questionNumber || '0'}.
                              </span>
                              <span className="text-sm leading-relaxed">
                                {ex.questionText || ex.question}
                              </span>
                            </div>
                          </div>

                          {/* Multiple Choice Options (Disabled) */}
                          <div className="space-y-2 pl-6">
                            {ex.options && ex.options.map((opt, index) => (
                              <label
                                key={opt.id}
                                className={`
                                  flex items-start p-3 border-2 rounded-none transition-all cursor-not-allowed
                                  ${correctAnswer === opt.id
                                    ? 'border-gray-900 bg-gray-900 text-white'
                                    : 'border-gray-400 bg-white'
                                  }
                                `}
                              >
                                <span className={`font-semibold w-5 flex-shrink-0 ${correctAnswer === opt.id ? 'text-white' : 'text-gray-900'}`}>
                                  {String.fromCharCode(97 + index)}.
                                </span>
                                <span className={`flex-1 ml-3 ${correctAnswer === opt.id ? 'text-white' : 'text-gray-900'}`}>{opt.text}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Regular Questions */}
                    {associatedQuestions.map(q => {
                      const currentAnswer = partAnswers[q.questionId];
                      return (
                        <div
                          key={q.questionId}
                          className="border border-t-0 border-gray-900 bg-white p-4 rounded-none hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1 flex items-start">
                              <span className="font-bold text-sm mr-2 pt-0.5">
                                {q.questionNumber}.
                              </span>
                              <span className="text-sm leading-relaxed">
                                {q.questionText || q.question}
                              </span>
                            </div>
                            <FlagButton {...allProps} questionId={q.questionId} />
                          </div>

                          {/* Multiple Choice Options */}
                          <div className="space-y-2 pl-6">
                            {q.options.map((opt, index) => (
                              <label
                                key={opt.id}
                                onClick={() => handleSingleAnswer(q.questionId, opt.id)}
                                className={`
                                  flex items-start p-3 border-2 rounded-none transition-all cursor-pointer
                                  ${currentAnswer === opt.id
                                    ? 'border-gray-900 bg-gray-900 text-white'
                                    : 'border-gray-400 bg-white hover:border-gray-900 hover:shadow-md hover:-translate-y-0.5' 
                                  }
                                `}
                              >
                                <span className={`font-semibold w-5 flex-shrink-0 ${currentAnswer === opt.id ? 'text-white' : 'text-gray-900'}`}>
                                  {String.fromCharCode(97 + index)}.
                                </span>
                                <span className={`flex-1 ml-3 ${currentAnswer === opt.id ? 'text-white' : 'text-gray-900'}`}>{opt.text}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SplitArticleComprehension;